import { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { Pen, Eraser, Trash2, ArrowUp, MousePointer2, RotateCcw, RotateCw, Maximize2, Minimize2, Type } from "lucide-react";

const CW = 600, CH = 748;        // risoluzione interna del canvas (display 300x374 → scala 2x)
const DISP_W = 300, DISP_H = 374;
// Rapporto d'aspetto allineato all'area "13. grafico" del PDF (66mm x ~82.2mm): così
// l'immagine del croquis riempie la griglia del PDF senza deformazioni (WYSIWYG).
const GRID_W_MM = 66, GRID_H_MM = 82.2;

const clone = (o) => JSON.parse(JSON.stringify(o || []));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
let _sid = 0;
const nid = () => `s${Date.now().toString(36)}${_sid++}`;

// --- Disegno sagome: ogni funzione disegna centrata in (0,0) entro un box w x h ---
function rr(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const DRAW = {
  car(ctx, w, h, color) {
    ctx.lineWidth = 2; ctx.strokeStyle = "#111827"; ctx.fillStyle = color || "#cfd8e3";
    // Anteriore in alto (-y): angoli anteriori più arrotondati, posteriore più squadrato.
    const rf = Math.min(w * 0.46, h * 0.3), rb = Math.min(w * 0.18, h * 0.14);
    ctx.beginPath();
    ctx.moveTo(-w / 2 + rf, -h / 2);
    ctx.lineTo(w / 2 - rf, -h / 2);
    ctx.arcTo(w / 2, -h / 2, w / 2, -h / 2 + rf, rf);
    ctx.lineTo(w / 2, h / 2 - rb);
    ctx.arcTo(w / 2, h / 2, w / 2 - rb, h / 2, rb);
    ctx.lineTo(-w / 2 + rb, h / 2);
    ctx.arcTo(-w / 2, h / 2, -w / 2, h / 2 - rb, rb);
    ctx.lineTo(-w / 2, -h / 2 + rf);
    ctx.arcTo(-w / 2, -h / 2, -w / 2 + rf, -h / 2, rf);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    // Parabrezza (anteriore): trapezio con lato corto verso il cofano in alto.
    ctx.fillStyle = "#64748b";
    ctx.beginPath();
    ctx.moveTo(-w * 0.26, -h * 0.04); ctx.lineTo(w * 0.26, -h * 0.04);
    ctx.lineTo(w * 0.18, -h * 0.22); ctx.lineTo(-w * 0.18, -h * 0.22);
    ctx.closePath(); ctx.fill();
    // Lunotto posteriore.
    rr(ctx, -w * 0.28, h * 0.12, w * 0.56, h * 0.16, 2); ctx.fill();
  },
  van(ctx, w, h, color) {
    ctx.lineWidth = 2; ctx.strokeStyle = "#111827"; ctx.fillStyle = color || "#d6e0ec";
    rr(ctx, -w / 2, -h / 2, w, h, 4); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-w / 2, -h * 0.16); ctx.lineTo(w / 2, -h * 0.16); ctx.stroke();
    ctx.fillStyle = "#64748b"; rr(ctx, -w * 0.34, -h * 0.42, w * 0.68, h * 0.16, 2); ctx.fill();
  },
  moto(ctx, w, h, color) {
    ctx.lineWidth = 1.6; ctx.strokeStyle = "#111827"; ctx.fillStyle = color || "#334155";
    // Corpo affusolato (serbatoio/sella), con punta verso l'anteriore (in alto).
    ctx.beginPath();
    ctx.moveTo(0, -h * 0.34);
    ctx.quadraticCurveTo(w * 0.5, -h * 0.12, w * 0.3, h * 0.32);
    ctx.lineTo(-w * 0.3, h * 0.32);
    ctx.quadraticCurveTo(-w * 0.5, -h * 0.12, 0, -h * 0.34);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    // Ruota anteriore (in alto).
    ctx.fillStyle = "#1f2937";
    rr(ctx, -w * 0.13, -h * 0.5, w * 0.26, h * 0.18, Math.min(w, h) * 0.05); ctx.fill(); ctx.stroke();
    // Manubrio (anteriore): barra trasversale larga → rende riconoscibile il davanti.
    ctx.strokeStyle = color || "#111827"; ctx.lineWidth = 2.2;
    ctx.beginPath(); ctx.moveTo(-w * 0.45, -h * 0.34); ctx.lineTo(w * 0.45, -h * 0.34); ctx.stroke();
    // Ruota posteriore (in basso).
    ctx.strokeStyle = "#111827"; ctx.lineWidth = 1.6; ctx.fillStyle = "#1f2937";
    rr(ctx, -w * 0.13, h * 0.32, w * 0.26, h * 0.18, Math.min(w, h) * 0.05); ctx.fill(); ctx.stroke();
  },
  truck(ctx, w, h, color) {
    ctx.lineWidth = 2; ctx.strokeStyle = "#111827";
    ctx.fillStyle = "#9fb0c3"; rr(ctx, -w / 2, -h / 2, w, h * 0.26, 3); ctx.fill(); ctx.stroke();
    ctx.fillStyle = color || "#e2e8f0"; rr(ctx, -w / 2, -h / 2 + h * 0.3, w, h * 0.7, 3); ctx.fill(); ctx.stroke();
  },
  bicycle(ctx, w, h, color) {
    ctx.strokeStyle = color || "#111827"; ctx.lineWidth = 1.5;
    const r = w * 0.5;
    ctx.beginPath(); ctx.arc(0, -h / 2 + r, r, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, h / 2 - r, r, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, -h / 2 + r); ctx.lineTo(0, h / 2 - r); ctx.stroke();
  },
  stop(ctx, w, h) {
    const s = Math.min(w, h) / 2;
    ctx.fillStyle = "#d81e1e"; ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < 8; i++) { const a = Math.PI / 8 + i * Math.PI / 4; const px = Math.cos(a) * s, py = Math.sin(a) * s; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); }
    ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#ffffff"; ctx.font = `bold ${s * 0.5}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("STOP", 0, 0);
  },
  yield(ctx, w, h) {
    const s = Math.min(w, h) / 2;
    ctx.fillStyle = "#ffffff"; ctx.strokeStyle = "#d81e1e"; ctx.lineWidth = Math.max(2, s * 0.16);
    ctx.beginPath(); ctx.moveTo(-s, -s * 0.86); ctx.lineTo(s, -s * 0.86); ctx.lineTo(0, s); ctx.closePath();
    ctx.fill(); ctx.stroke();
  },
  semaforo(ctx, w, h) {
    ctx.fillStyle = "#1f2937"; rr(ctx, -w / 2, -h / 2, w, h, w * 0.2); ctx.fill();
    const r = w * 0.26, ys = [-h * 0.3, 0, h * 0.3], cols = ["#e53935", "#fdd835", "#43a047"];
    ys.forEach((yy, i) => { ctx.fillStyle = cols[i]; ctx.beginPath(); ctx.arc(0, yy, r, 0, Math.PI * 2); ctx.fill(); });
  },
  arrow(ctx, w, h) {
    ctx.fillStyle = "#1565c0"; ctx.strokeStyle = "#0d47a1"; ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, -h / 2);
    ctx.lineTo(w / 2, -h * 0.05);
    ctx.lineTo(w * 0.22, -h * 0.05);
    ctx.lineTo(w * 0.22, h / 2);
    ctx.lineTo(-w * 0.22, h / 2);
    ctx.lineTo(-w * 0.22, -h * 0.05);
    ctx.lineTo(-w / 2, -h * 0.05);
    ctx.closePath(); ctx.fill(); ctx.stroke();
  },
  crosswalk(ctx, w, h) {
    ctx.fillStyle = "#f8fafc"; ctx.strokeStyle = "#475569"; ctx.lineWidth = 0.6;
    const n = 5, bw = w / (n * 2 - 1);
    for (let i = 0; i < n; i++) { const x = -w / 2 + i * 2 * bw; ctx.fillRect(x, -h / 2, bw, h); ctx.strokeRect(x, -h / 2, bw, h); }
  },
  centerline(ctx, w, h) {
    ctx.fillStyle = "#f1c40f";
    const seg = h * 0.18, gap = h * 0.12; let y = -h / 2;
    while (y < h / 2) { ctx.fillRect(-w / 2, y, w, Math.min(seg, h / 2 - y)); y += seg + gap; }
  },
  // --- Strade (vista dall'alto) ---
  road(ctx, w, h) { roadRect(ctx, -w / 2, -h / 2, w, h, "v"); },
  road2(ctx, w, h) { roadRect(ctx, -w / 2, -h / 2, w, h, "v", true); },
  cross(ctx, w, h) {
    const bw = Math.min(w, h) * 0.34;
    roadRect(ctx, -bw / 2, -h / 2, bw, h, "v"); roadRect(ctx, -w / 2, -bw / 2, w, bw, "h");
    ctx.fillStyle = ASPH; ctx.fillRect(-bw / 2, -bw / 2, bw, bw);
  },
  tjunction(ctx, w, h) {
    const bw = Math.min(w, h) * 0.34;
    roadRect(ctx, -w / 2, -h / 2, w, bw, "h"); roadRect(ctx, -bw / 2, -h / 2 + bw, bw, h - bw, "v");
    ctx.fillStyle = ASPH; ctx.fillRect(-bw / 2, -h / 2 + 1.5, bw, bw - 1.5);
  },
  curve(ctx, w, h) {
    const bw = Math.min(w, h) * 0.34, R = Math.min(w, h) - bw / 2;
    ctx.save(); ctx.translate(-w / 2, h / 2);
    ctx.lineWidth = bw; ctx.strokeStyle = ASPH; ctx.beginPath(); ctx.arc(0, 0, R, -Math.PI / 2, 0); ctx.stroke();
    ctx.lineWidth = 1.5; ctx.strokeStyle = "#ffffff";
    [R - bw / 2 + 2, R + bw / 2 - 2].forEach((r) => { ctx.beginPath(); ctx.arc(0, 0, r, -Math.PI / 2, 0); ctx.stroke(); });
    ctx.setLineDash([8, 7]); ctx.beginPath(); ctx.arc(0, 0, R, -Math.PI / 2, 0); ctx.stroke(); ctx.setLineDash([]);
    ctx.restore();
  },
  roundabout(ctx, w, h) {
    const r = Math.min(w, h) / 2, bw = r * 0.42;
    ctx.lineWidth = bw; ctx.strokeStyle = ASPH; ctx.beginPath(); ctx.arc(0, 0, r - bw / 2, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = "#86c06c"; ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(0, 0, r - bw, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, 0, r - 1.5, 0, Math.PI * 2); ctx.stroke();
  },
  // --- Segnaletica aggiuntiva ---
  noentry(ctx, w, h) {
    const s = Math.min(w, h) / 2;
    ctx.fillStyle = "#d81e1e"; ctx.beginPath(); ctx.arc(0, 0, s, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.fillRect(-s * 0.65, -s * 0.16, s * 1.3, s * 0.32);
  },
  oneway(ctx, w, h) {
    ctx.fillStyle = "#1565c0"; rr(ctx, -w / 2, -h / 2, w, h, 3); ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.beginPath();
    ctx.moveTo(w * 0.4, 0); ctx.lineTo(w * 0.12, -h * 0.34); ctx.lineTo(w * 0.12, -h * 0.14); ctx.lineTo(-w * 0.4, -h * 0.14);
    ctx.lineTo(-w * 0.4, h * 0.14); ctx.lineTo(w * 0.12, h * 0.14); ctx.lineTo(w * 0.12, h * 0.34); ctx.closePath(); ctx.fill();
  },
  priority(ctx, w, h) {
    const s = Math.min(w, h) / 2;
    ctx.fillStyle = "#ffffff"; ctx.strokeStyle = "#111827"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s, 0); ctx.lineTo(0, s); ctx.lineTo(-s, 0); ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#f5c400"; const k = s * 0.6;
    ctx.beginPath(); ctx.moveTo(0, -k); ctx.lineTo(k, 0); ctx.lineTo(0, k); ctx.lineTo(-k, 0); ctx.closePath(); ctx.fill();
  },
  speed(ctx, w, h, _c, sh) {
    const s = Math.min(w, h) / 2;
    const lim = String((sh && sh.speed) || "50");
    ctx.fillStyle = "#ffffff"; ctx.strokeStyle = "#d81e1e"; ctx.lineWidth = s * 0.2;
    ctx.beginPath(); ctx.arc(0, 0, s - s * 0.1, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#111827"; ctx.font = `bold ${s * (lim.length > 2 ? 0.58 : 0.75)}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(lim, 0, s * 0.04);
  },
  rotsign(ctx, w, h) {
    const s = Math.min(w, h) / 2;
    ctx.fillStyle = "#1565c0"; ctx.beginPath(); ctx.arc(0, 0, s, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "#ffffff"; ctx.fillStyle = "#ffffff"; ctx.lineWidth = s * 0.16;
    for (let i = 0; i < 3; i++) {
      const a0 = i * (2 * Math.PI / 3) - Math.PI / 2;
      ctx.beginPath(); ctx.arc(0, 0, s * 0.5, a0 + 0.35, a0 + 1.65); ctx.stroke();
      const a = a0 + 1.75, px = Math.cos(a) * s * 0.5, py = Math.sin(a) * s * 0.5;
      ctx.save(); ctx.translate(px, py); ctx.rotate(a + Math.PI); ctx.beginPath(); ctx.moveTo(0, -s * 0.2); ctx.lineTo(s * 0.18, s * 0.12); ctx.lineTo(-s * 0.18, s * 0.12); ctx.closePath(); ctx.fill(); ctx.restore();
    }
  },
  pedcross(ctx, w, h) {
    ctx.fillStyle = "#1565c0"; rr(ctx, -w / 2, -h / 2, w, h, 3); ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.moveTo(0, -h * 0.4); ctx.lineTo(w * 0.4, h * 0.36); ctx.lineTo(-w * 0.4, h * 0.36); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#111827"; ctx.beginPath(); ctx.arc(0, -h * 0.08, w * 0.06, 0, Math.PI * 2); ctx.fill();
    ctx.fillRect(-w * 0.03, -h * 0.02, w * 0.06, h * 0.22);
    ctx.fillRect(-w * 0.2, h * 0.24, w * 0.4, h * 0.04);
  },
  parking(ctx, w, h) {
    ctx.fillStyle = "#1565c0"; rr(ctx, -w / 2, -h / 2, w, h, 3); ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.font = `bold ${Math.min(w, h) * 0.75}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("P", 0, h * 0.04);
  },
  stopline(ctx, w, h) { ctx.fillStyle = "#111827"; ctx.fillRect(-w / 2, -h / 2, w, h); },
};

const ASPH = "#6b7280";
// Tratto di strada rettilineo: asfalto, bordi bianchi continui, mezzeria tratteggiata (o doppia continua).
function roadRect(ctx, x, y, w, h, dir, doppia = false) {
  ctx.fillStyle = ASPH; ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 1.5;
  ctx.beginPath();
  if (dir === "v") { ctx.moveTo(x + 2, y); ctx.lineTo(x + 2, y + h); ctx.moveTo(x + w - 2, y); ctx.lineTo(x + w - 2, y + h); }
  else { ctx.moveTo(x, y + 2); ctx.lineTo(x + w, y + 2); ctx.moveTo(x, y + h - 2); ctx.lineTo(x + w, y + h - 2); }
  ctx.stroke();
  const mx = x + w / 2, my = y + h / 2;
  if (doppia) {
    ctx.beginPath();
    if (dir === "v") { ctx.moveTo(mx - 2, y); ctx.lineTo(mx - 2, y + h); ctx.moveTo(mx + 2, y); ctx.lineTo(mx + 2, y + h); }
    else { ctx.moveTo(x, my - 2); ctx.lineTo(x + w, my - 2); ctx.moveTo(x, my + 2); ctx.lineTo(x + w, my + 2); }
    ctx.stroke();
  } else {
    ctx.setLineDash([10, 8]); ctx.beginPath();
    if (dir === "v") { ctx.moveTo(mx, y); ctx.lineTo(mx, y + h); } else { ctx.moveTo(x, my); ctx.lineTo(x + w, my); }
    ctx.stroke(); ctx.setLineDash([]);
  }
}

const ROAD_TYPES = ["road", "road2", "cross", "tjunction", "curve", "roundabout"];

const SHAPES = [
  { type: "road", label: "Strada", cat: "Strade", w: 90, h: 400 },
  { type: "road2", label: "Linea continua", cat: "Strade", w: 90, h: 400 },
  { type: "cross", label: "Incrocio", cat: "Strade", w: 400, h: 400 },
  { type: "tjunction", label: "Incrocio a T", cat: "Strade", w: 400, h: 320 },
  { type: "curve", label: "Curva", cat: "Strade", w: 260, h: 260 },
  { type: "roundabout", label: "Rotatoria", cat: "Strade", w: 300, h: 300 },
  { type: "car", label: "Auto", cat: "Veicoli", w: 40, h: 80 },
  { type: "van", label: "Furgone", cat: "Veicoli", w: 44, h: 96 },
  { type: "moto", label: "Moto", cat: "Veicoli", w: 22, h: 58 },
  { type: "truck", label: "Camion", cat: "Veicoli", w: 48, h: 130 },
  { type: "bicycle", label: "Bici", cat: "Veicoli", w: 18, h: 52 },
  { type: "stop", label: "Stop", cat: "Segnaletica", w: 46, h: 46 },
  { type: "yield", label: "Precedenza", cat: "Segnaletica", w: 50, h: 46 },
  { type: "semaforo", label: "Semaforo", cat: "Segnaletica", w: 26, h: 64 },
  { type: "arrow", label: "Freccia", cat: "Segnaletica", w: 34, h: 70 },
  { type: "crosswalk", label: "Strisce", cat: "Segnaletica", w: 70, h: 40 },
  { type: "centerline", label: "Mezzeria", cat: "Segnaletica", w: 12, h: 120 },
  { type: "stopline", label: "Linea arresto", cat: "Segnaletica", w: 90, h: 6 },
  { type: "noentry", label: "Divieto accesso", cat: "Segnaletica", w: 44, h: 44 },
  { type: "oneway", label: "Senso unico", cat: "Segnaletica", w: 64, h: 32 },
  { type: "priority", label: "Diritto precedenza", cat: "Segnaletica", w: 46, h: 46 },
  { type: "rotsign", label: "Rotatoria", cat: "Segnaletica", w: 44, h: 44 },
  { type: "speed", label: "Limite velocità", cat: "Segnaletica", w: 44, h: 44 },
  { type: "pedcross", label: "Pedoni", cat: "Segnaletica", w: 40, h: 40 },
  { type: "parking", label: "Parcheggio", cat: "Segnaletica", w: 36, h: 36 },
];
const META = Object.fromEntries(SHAPES.map((s) => [s.type, s]));
const CATS = ["Strade", "Veicoli", "Segnaletica"];
// Tipi di veicolo per cui è possibile cambiare il colore della sagoma.
const VEHICLE_TYPES = ["car", "van", "moto", "truck", "bicycle"];
// Palette di colori comuni per le sagome veicolo.
const SHAPE_COLORS = ["#ffffff", "#111827", "#dc2626", "#2563eb", "#16a34a", "#f59e0b", "#9ca3af", "#7c3aed"];

function drawShape(ctx, s) {
  ctx.save();
  ctx.translate(s.cx, s.cy);
  ctx.rotate(s.rot || 0);
  if (s.type === "text") {
    ctx.fillStyle = s.color || "#111827";
    ctx.font = `bold ${s.fontSize || 30}px sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(s.text || "", 0, 0);
  } else {
    (DRAW[s.type] || (() => {}))(ctx, s.w, s.h, s.color, s);
  }
  ctx.restore();
}

function toLocal(s, px, py) {
  const dx = px - s.cx, dy = py - s.cy, cos = Math.cos(s.rot || 0), sin = Math.sin(s.rot || 0);
  return { x: dx * cos + dy * sin, y: -dx * sin + dy * cos };
}
function toWorld(s, lx, ly) {
  const cos = Math.cos(s.rot || 0), sin = Math.sin(s.rot || 0);
  return { x: s.cx + lx * cos - ly * sin, y: s.cy + lx * sin + ly * cos };
}

function ShapePreview({ type }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height);
    const m = META[type]; const pad = 7;
    const k = Math.min((c.width - 2 * pad) / m.w, (c.height - 2 * pad) / m.h);
    ctx.save(); ctx.translate(c.width / 2, c.height / 2); ctx.scale(k, k); (DRAW[type])(ctx, m.w, m.h); ctx.restore();
  }, [type]);
  return <canvas ref={ref} width={38} height={38} className="pointer-events-none" />;
}

// Editor del croquis: disegno libero + libreria sagome/simboli trascinabili, con rotazione e ridimensionamento.
export default function CroquisEditor({ value, drawValue, shapes: shapesProp, onChange, dinamica, dinamicaFont }) {
  const canvasRef = useRef(null);
  const freeRef = useRef(null);        // canvas offscreen col solo disegno libero
  const shapesRef = useRef(clone(shapesProp));
  const drawing = useRef(false);
  const last = useRef(null);
  const gesture = useRef(null);
  const selRef = useRef(null);

  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#111111");
  const [width, setWidth] = useState(3);
  const [selId, setSelId] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => { selRef.current = selId; }, [selId]);

  const drawHandles = (ctx, s) => {
    ctx.save(); ctx.translate(s.cx, s.cy); ctx.rotate(s.rot || 0);
    ctx.strokeStyle = "#0ea5e9"; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4]);
    ctx.strokeRect(-s.w / 2, -s.h / 2, s.w, s.h); ctx.setLineDash([]);
    ctx.fillStyle = "#0ea5e9"; ctx.fillRect(s.w / 2 - 7, s.h / 2 - 7, 14, 14);
    ctx.beginPath(); ctx.moveTo(0, -s.h / 2); ctx.lineTo(0, -s.h / 2 - 22); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -s.h / 2 - 24, 8, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  };

  const redraw = useCallback((withHandles = true) => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, CW, CH);
    if (freeRef.current) ctx.drawImage(freeRef.current, 0, 0);
    shapesRef.current.filter((s) => ROAD_TYPES.includes(s.type)).forEach((s) => drawShape(ctx, s));
    shapesRef.current.filter((s) => !ROAD_TYPES.includes(s.type)).forEach((s) => drawShape(ctx, s));
    if (withHandles) { const s = shapesRef.current.find((x) => x.id === selRef.current); if (s) drawHandles(ctx, s); }
  }, []);

  const commit = useCallback(() => {
    redraw(false);
    const flat = canvasRef.current.toDataURL("image/png");
    const draw = freeRef.current.toDataURL("image/png");
    redraw(true);
    onChange({ croquis: flat, draw, shapes: clone(shapesRef.current) });
  }, [onChange, redraw]);

  // init una sola volta
  useEffect(() => {
    const free = document.createElement("canvas"); free.width = CW; free.height = CH; freeRef.current = free;
    const src = drawValue || value;
    if (src) { const img = new Image(); img.onload = () => { free.getContext("2d").drawImage(img, 0, 0, CW, CH); redraw(true); }; img.src = src; }
    else { redraw(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const evtPos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - r.left) * (CW / r.width), y: (cy - r.top) * (CH / r.height) };
  };

  const addShape = (type, x = CW / 2, y = CH / 2) => {
    const m = META[type]; if (!m) return;
    let extra = {};
    if (type === "speed") {
      const v = window.prompt("Limite di velocità (km/h):", "50");
      if (v === null) return;
      extra = { speed: String(v).replace(/\D/g, "").slice(0, 3) || "50" };
    }
    const s = { id: nid(), type, cx: clamp(x, 10, CW - 10), cy: clamp(y, 10, CH - 10), w: m.w, h: m.h, rot: 0, ...extra };
    shapesRef.current = [...shapesRef.current, s];
    setTool("select"); selRef.current = s.id; setSelId(s.id);
    redraw(true); commit();
  };

  // Misura il box (larghezza/altezza) di un testo con il font corrente del canvas.
  const measureText = (text, fontSize) => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.save(); ctx.font = `bold ${fontSize}px sans-serif`;
    const w = Math.max(16, ctx.measureText(text || " ").width);
    ctx.restore();
    return { w, h: fontSize * 1.2 };
  };

  // Inserisce un testo libero come elemento selezionabile (spostabile/ridimensionabile/ruotabile).
  const addText = (text, x = CW / 2, y = CH / 2) => {
    const fontSize = 30;
    const { w, h } = measureText(text, fontSize);
    const s = { id: nid(), type: "text", text, fontSize, color, cx: clamp(x, 10, CW - 10), cy: clamp(y, 10, CH - 10), w, h, rot: 0 };
    shapesRef.current = [...shapesRef.current, s];
    setTool("select"); selRef.current = s.id; setSelId(s.id);
    redraw(true); commit();
  };

  const onDown = (e) => {
    if (tool === "pen" || tool === "eraser") {
      e.preventDefault(); drawing.current = true; last.current = evtPos(e); return;
    }
    if (tool === "text") {
      const tp = evtPos(e);
      const txt = window.prompt("Testo da inserire:", "");
      if (txt && txt.trim()) addText(txt.trim(), tp.x, tp.y);
      return;
    }
    const p = evtPos(e);
    const cur = shapesRef.current.find((x) => x.id === selRef.current);
    if (cur) {
      const rw = toWorld(cur, 0, -cur.h / 2 - 24);
      if (dist(p, rw) <= 13) { gesture.current = { mode: "rotate", s: cur }; return; }
      const zw = toWorld(cur, cur.w / 2, cur.h / 2);
      if (dist(p, zw) <= 14) { gesture.current = { mode: "resize", s: cur, ow: cur.w, oh: cur.h, ofs: cur.fontSize || 30, sd: Math.max(1, dist(p, { x: cur.cx, y: cur.cy })) }; return; }
    }
    const hit = (s) => { const l = toLocal(s, p.x, p.y); return Math.abs(l.x) <= s.w / 2 && Math.abs(l.y) <= s.h / 2; };
    const order = [...shapesRef.current.filter((s) => !ROAD_TYPES.includes(s.type)).reverse(), ...shapesRef.current.filter((s) => ROAD_TYPES.includes(s.type)).reverse()];
    for (let i = 0; i < order.length; i++) {
      const s = order[i];
      if (hit(s)) {
        selRef.current = s.id; setSelId(s.id);
        gesture.current = { mode: "move", s, ox: p.x - s.cx, oy: p.y - s.cy };
        redraw(true); return;
      }
    }
    selRef.current = null; setSelId(null); redraw(true);
  };

  const onMove = (e) => {
    if ((tool === "pen" || tool === "eraser") && drawing.current) {
      e.preventDefault();
      const ctx = freeRef.current.getContext("2d"); const p = evtPos(e);
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      if (tool === "eraser") { ctx.globalCompositeOperation = "destination-out"; ctx.lineWidth = width * 5; }
      else { ctx.globalCompositeOperation = "source-over"; ctx.strokeStyle = color; ctx.lineWidth = width; }
      ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      ctx.globalCompositeOperation = "source-over";
      last.current = p; redraw(true); return;
    }
    const g = gesture.current; if (!g) return;
    e.preventDefault(); const p = evtPos(e); const s = g.s;
    if (g.mode === "move") { s.cx = clamp(p.x - g.ox, 0, CW); s.cy = clamp(p.y - g.oy, 0, CH); }
    else if (g.mode === "rotate") { s.rot = Math.atan2(p.y - s.cy, p.x - s.cx) + Math.PI / 2; }
    else if (g.mode === "resize") {
      const k = clamp(dist(p, { x: s.cx, y: s.cy }) / g.sd, 0.15, 12);
      if (s.type === "text") { s.fontSize = clamp(g.ofs * k, 8, 200); const m = measureText(s.text, s.fontSize); s.w = m.w; s.h = m.h; }
      else { s.w = clamp(g.ow * k, 8, CW); s.h = clamp(g.oh * k, 8, CH); }
    }
    redraw(true);
  };

  const onUp = () => {
    if (drawing.current) { drawing.current = false; commit(); return; }
    if (gesture.current) { gesture.current = null; commit(); }
  };

  const updateSel = (fn) => {
    const s = shapesRef.current.find((x) => x.id === selRef.current); if (!s) return;
    fn(s); redraw(true); commit();
  };
  const rotateSel = (deg) => updateSel((s) => { s.rot = (s.rot || 0) + deg * Math.PI / 180; });
  const setSelColor = (hex) => { updateSel((s) => { s.color = hex; }); setTick((t) => t + 1); };
  const scaleSel = (f) => updateSel((s) => {
    if (s.type === "text") { s.fontSize = clamp((s.fontSize || 30) * f, 8, 200); const m = measureText(s.text, s.fontSize); s.w = m.w; s.h = m.h; }
    else { s.w = clamp(s.w * f, 8, CW); s.h = clamp(s.h * f, 8, CH); }
  });
  const deleteSel = () => {
    if (!selRef.current) return;
    shapesRef.current = shapesRef.current.filter((x) => x.id !== selRef.current);
    selRef.current = null; setSelId(null); redraw(true); commit();
  };
  const clearAll = () => {
    freeRef.current.getContext("2d").clearRect(0, 0, CW, CH);
    shapesRef.current = []; selRef.current = null; setSelId(null);
    redraw(true); onChange({ croquis: "", draw: "", shapes: [] });
  };

  const onDrop = (e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("croquis-shape"); if (!type) return;
    const p = evtPos(e); addShape(type, p.x, p.y);
  };

  const tbBtn = (active) => `flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${active ? "bg-teal-600 border-teal-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}`;
  const COLORS = [["#111111", "nero"], ["#dc2626", "rosso"], ["#2563eb", "blu"]];
  const WIDTHS = [["sottile", 2], ["medio", 4], ["spesso", 7]];

  const selShape = shapesRef.current.find((x) => x.id === selId);
  const showColor = !!(selShape && VEHICLE_TYPES.includes(selShape.type));

  // Anteprima in tempo reale della dinamica sopra il grafico: dimensione, posizione, colore
  // e n. righe identici al PDF. Nel PDF il testo è reso dal proxy in GRASSETTO NERO (helvetica),
  // con fs in punti → mm (×0.3528). Scala px/mm = DISP_W/66 (griglia 66mm).
  const dinPt = Math.max(10, Math.min(18, Number(dinamicaFont) || 14));
  const dinPxPerMm = DISP_W / GRID_W_MM;
  const dinMaxLines = dinPt <= 12 ? 6 : dinPt <= 15 ? 5 : 4;
  const dinAvailPx = dinMaxLines * dinPt * 0.5 * 0.3528 * 1.15 * dinPxPerMm; // altezza disponibile come nel PDF
  const dinText = (dinamica || "").trim();
  // Testo lungo: riduce il carattere (passi 0,5pt, minimo 7pt) finché la dinamica entra per intero.
  const dinRef = useRef(null);
  const [dinFitPt, setDinFitPt] = useState(dinPt);
  useLayoutEffect(() => {
    const el = dinRef.current;
    if (!el) { setDinFitPt(dinPt); return; }
    let pt = dinPt;
    for (; pt > 7; pt -= 0.5) {
      el.style.fontSize = `${pt * 0.5 * 0.3528 * dinPxPerMm}px`;
      el.style.lineHeight = `${pt * 0.5 * 0.3528 * 1.15 * dinPxPerMm}px`;
      if (el.scrollHeight <= dinAvailPx + 1) break;
    }
    setDinFitPt(pt);
  }, [dinText, dinPt, dinPxPerMm, dinAvailPx]);
  const dinFsMm = dinFitPt * 0.5 * 0.3528;           // fs jsPDF (punti) → mm
  const dinFontPx = dinFsMm * dinPxPerMm;
  const dinLineMm = dinFitPt * 0.5 * 0.3528 * 1.15;  // interlinea come nel PDF
  const dinLinePx = dinLineMm * dinPxPerMm;
  const dinTopPx = Math.max(0, (8 - dinLineMm) * dinPxPerMm); // blocco testo a (8mm - interlinea) dal top griglia
  const dinLeftPx = 1.5 * dinPxPerMm;
  const dinWidthPx = (GRID_W_MM - 3) * dinPxPerMm;

  return (
    <div data-testid="croquis-editor">
      {/* Toolbar strumenti */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button type="button" data-testid="croquis-tool-select" onClick={() => setTool("select")} className={tbBtn(tool === "select")}><MousePointer2 className="h-3.5 w-3.5" /> Seleziona</button>
        <button type="button" data-testid="croquis-tool-pen" onClick={() => { setTool("pen"); setSelId(null); selRef.current = null; redraw(true); }} className={tbBtn(tool === "pen")}><Pen className="h-3.5 w-3.5" /> Penna</button>
        <button type="button" data-testid="croquis-tool-eraser" onClick={() => { setTool("eraser"); setSelId(null); selRef.current = null; redraw(true); }} className={tbBtn(tool === "eraser")}><Eraser className="h-3.5 w-3.5" /> Gomma</button>
        <button type="button" data-testid="croquis-tool-text" onClick={() => { setTool("text"); setSelId(null); selRef.current = null; redraw(true); }} className={tbBtn(tool === "text")}><Type className="h-3.5 w-3.5" /> Testo</button>
        <span className="w-px h-5 bg-slate-700 mx-1" />
        {COLORS.map(([hex, name]) => (
          <button key={hex} type="button" data-testid={`croquis-color-${name}`} title={name}
            onClick={() => { setColor(hex); setTool("pen"); }}
            className={`h-6 w-6 rounded-full border-2 ${color === hex && tool === "pen" ? "border-teal-400 scale-110" : "border-slate-600"}`}
            style={{ backgroundColor: hex }} />
        ))}
        <span className="w-px h-5 bg-slate-700 mx-1" />
        {WIDTHS.map(([name, px]) => (
          <button key={name} type="button" data-testid={`croquis-width-${name}`} onClick={() => { setWidth(px); setTool("pen"); }} className={tbBtn(width === px && tool === "pen")}>{name}</button>
        ))}
        <span className="w-px h-5 bg-slate-700 mx-1" />
        <button type="button" data-testid="croquis-clear" onClick={clearAll} className={tbBtn(false)}><Trash2 className="h-3.5 w-3.5" /> Cancella tutto</button>
      </div>

      <div className="flex gap-3">
        {/* Pannello laterale libreria sagome */}
        <div className="w-40 shrink-0 rounded-md border border-slate-700 bg-slate-900/40 p-2 max-h-[385px] overflow-y-auto" data-testid="croquis-library">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Trascina sul disegno</div>
          {CATS.map((cat) => (
            <div key={cat} className="mb-2">
              <div className="text-[11px] font-semibold text-teal-300 mb-1">{cat}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {SHAPES.filter((s) => s.cat === cat).map((s) => (
                  <button key={s.type} type="button" data-testid={`croquis-add-${s.type}`}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("croquis-shape", s.type)}
                    onClick={() => addShape(s.type)}
                    title={`Aggiungi ${s.label}`}
                    className="flex flex-col items-center justify-center gap-0.5 rounded border border-slate-700 bg-slate-800/60 hover:bg-slate-700 hover:border-teal-500/60 py-1.5 cursor-grab active:cursor-grabbing">
                    <ShapePreview type={s.type} />
                    <span className="text-[9px] text-slate-300 leading-none">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Area canvas */}
        <div className="flex-1">
          {selId && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="croquis-selection-controls">
              <span className="text-[10px] text-slate-400 mr-1">Elemento selezionato</span>
              <button type="button" data-testid="croquis-rotate-left" onClick={() => rotateSel(-15)} title="Ruota a sinistra" className={tbBtn(false)}><RotateCcw className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid="croquis-rotate-right" onClick={() => rotateSel(15)} title="Ruota a destra" className={tbBtn(false)}><RotateCw className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid="croquis-bigger" onClick={() => scaleSel(1.15)} title="Ingrandisci" className={tbBtn(false)}><Maximize2 className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid="croquis-smaller" onClick={() => scaleSel(0.87)} title="Riduci" className={tbBtn(false)}><Minimize2 className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid="croquis-delete" onClick={deleteSel} title="Elimina" className="flex items-center gap-1 px-2 py-1 rounded text-xs border border-red-500/50 text-red-300 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
              {selShape && selShape.type === "speed" && (
                <label className="inline-flex items-center gap-1 ml-1 pl-2 border-l border-slate-700 text-[10px] text-slate-400">
                  Limite km/h
                  <input type="text" inputMode="numeric" value={selShape.speed || "50"} data-testid="croquis-speed-value"
                    onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 3); updateSel((s) => { s.speed = v; }); setTick((t) => t + 1); }}
                    className="h-6 w-12 rounded border border-slate-600 bg-slate-900/60 px-1 text-xs text-slate-100 font-mono" />
                </label>
              )}
              {showColor && (
                <span className="inline-flex items-center gap-1 flex-wrap ml-1 pl-2 border-l border-slate-700" data-testid="croquis-shape-color-picker">
                  <span className="text-[10px] text-slate-400">Colore</span>
                  {SHAPE_COLORS.map((hex) => (
                    <button key={hex} type="button" data-testid={`croquis-shape-color-${hex}`} title={hex}
                      onClick={() => setSelColor(hex)}
                      className={`h-5 w-5 rounded-full border ${selShape.color === hex ? "border-teal-400 ring-1 ring-teal-400 scale-110" : "border-slate-500"}`}
                      style={{ backgroundColor: hex }} />
                  ))}
                  <input type="color" value={selShape.color || "#cfd8e3"} onChange={(e) => setSelColor(e.target.value)}
                    data-testid="croquis-shape-color-free" title="Colore personalizzato"
                    className="h-5 w-6 p-0 bg-transparent border border-slate-500 rounded cursor-pointer" />
                </span>
              )}
            </div>
          )}
          <div className="relative mx-auto bg-white border border-slate-400 rounded overflow-hidden" style={{ width: DISP_W, height: DISP_H }} data-testid="croquis-area">
            <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)", backgroundSize: "15px 15px" }} />
            {dinText && (
              <div data-testid="croquis-dinamica-overlay" ref={dinRef} data-fit-pt={dinFitPt}
                className="absolute pointer-events-none"
                style={{ left: `${dinLeftPx}px`, top: `${dinTopPx}px`, width: `${dinWidthPx}px`,
                  fontWeight: 700, fontStyle: "normal", color: "#000000",
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                  fontSize: `${dinFontPx}px`, lineHeight: `${dinLinePx}px`, background: "#ffffff",
                  whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {dinamica}
              </div>
            )}
            <div className="absolute bottom-1 right-1 flex flex-col items-center text-slate-500 pointer-events-none">
              <span className="text-[10px] font-bold leading-none">N</span>
              <ArrowUp className="h-4 w-4 -mt-0.5" strokeWidth={2.5} />
            </div>
            <canvas ref={canvasRef} width={CW} height={CH}
              data-testid="croquis-canvas"
              className={`absolute inset-0 w-full h-full touch-none ${tool === "select" ? "cursor-move" : "cursor-crosshair"}`}
              onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
              onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
              onDragOver={(e) => e.preventDefault()} onDrop={onDrop} />
          </div>
        </div>
      </div>
    </div>
  );
}
